/**
 * Widget Data Transformers
 * Contains data transformation logic for each widget type
 */

import { GROUPING_FIELDS, DEFAULT_FALLBACK_DATA, DATE_FORMATS } from './constants';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract data from API response using multiple possible paths
 * @param {Object} apiData - API response data
 * @param {string[]} possiblePaths - Array of dot-notation paths to try
 * @returns {Array} Extracted data array or empty array
 */
export const extractDataFromApiResponse = (apiData, possiblePaths) => {
  if (!apiData) return [];
  
  for (const path of possiblePaths) {
    const value = path.split('.').reduce((obj, key) => obj?.[key], apiData);
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
  }
  return [];
};

/**
 * Normalize grouping field from various possible field names
 * @param {Object} item - Data item
 * @param {string[]} possibleFields - Array of field names to try
 * @param {string} fallback - Fallback value if none found
 * @returns {string} Normalized grouping value
 */
export const normalizeGroupingField = (item, possibleFields, fallback = 'Unknown') => {
  if (!item) return fallback;
  
  for (const field of possibleFields) {
    const value = item[field];
    if (value !== undefined && value !== null && value !== '') {
      return String(value);
    }
  }
  return fallback;
};

/**
 * Extract time/date value from data item
 * @param {Object} item - Data item
 * @returns {string} ISO date string
 */
let warnCount = 0;
const MAX_WARNINGS = 10;

export const extractTimeValue = (item) => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const time = item?.time 
    || item?.date 
    || item?.groupings?.[0]?.value 
    || item?.period;

    if (!time) {
      if (warnCount < MAX_WARNINGS) {
        console.warn('Missing time/date field in data item, using today as fallback:', item);
        warnCount++;
        if (warnCount === MAX_WARNINGS) {
          console.warn('Max warnings reached, no more warnings will be logged');
        } 
      }
      return todayIso;
    }
    return time;
};

/**
 * Extract measure value from data item
 * @param {Object} item - Data item
 * @returns {number} Measure value
 */
export const extractMeasureValue = (item) => {
  return item?.measure ?? item?.cost ?? item?.value ?? item?.amount ?? 0;
};

/**
 * Safe data mapping wrapper with error handling
 * @param {Function} mapper - Data mapping function
 * @param {any} apiData - API data to transform
 * @param {Object} fallback - Fallback value on error
 * @returns {any} Transformed data or fallback
 */
export const safeDataMapping = (mapper, apiData, fallback = DEFAULT_FALLBACK_DATA) => {
  try {
    if (!apiData) {
      console.warn('No API data provided to data mapping');
      return fallback;
    }
    return mapper(apiData);
  } catch (error) {
    console.error('Data mapping error:', error);
    return fallback;
  }
};

/**
 * Calculate percentage change between current and previous periods
 * @param {Object} item - Data item with current_period and previous_period
 * @returns {number} Percentage change
 */
export const calculateChangePercentage = (item) => {
  const prev = item?.previous_period || 0;
  if (prev === 0) return 0;
  const curr = item?.current_period || 0;
  return ((curr - prev) / prev) * 100;
};

// ============================================================================
// Widget-Specific Transformers
// ============================================================================

/**
 * Transform data for KPI widget
 */
export const transformKPIData = (apiData) => safeDataMapping(
  (data) => ({
    currentValue: data.value || data.currentValue || 0,
    previousValue: data.previousValue || 0,
    trendData: data.trendData || null,
    totalValue: data.totalValue || null
  }),
  apiData,
  { currentValue: 0, previousValue: 0, trendData: null, totalValue: null }
);

/**
 * Specialized helper: transform ResourceChanges shape for Table widget
 * Keeps ResourceChanges-specific logic out of the main transformTableData function
 * @param {Object} data - Raw API data
 * @returns {{data: Array, grouping: string} | null}
 */
const transformResourceChangesForTable = (data) => {
  const resourceChanges = data?.ResourceChanges || data?.resourceChanges;
  if (!Array.isArray(resourceChanges) || resourceChanges.length === 0) {
    return null;
  }

  const transformed = resourceChanges.map((row) => ({
    ...row,
    grouping: row.resource_name || row.grouping || 'Unknown'
  }));

  return {
    data: transformed,
    grouping: data.grouping || 'resource_name'
  };
};

/**
 * Transform data for Table widget
 */
export const transformTableData = (apiData) => safeDataMapping(
  (data) => {
    // 1) If already in expected shape
    if (Array.isArray(data?.data)) {
      return { 
        data: data.data, 
        ...(data.grouping ? { grouping: data.grouping } : {}) 
      };
    }

    // 1.5) Handle ResourceChanges directly from API response (delegated helper)
    const resourceChangesResult = transformResourceChangesForTable(data);
    if (resourceChangesResult) {
      return resourceChangesResult;
    }

    // 2) Handle grouped_totals shape
    const groupedTotals = data?.CloudCost?.grouped_totals || data?.grouped_totals;
    if (Array.isArray(groupedTotals)) {
      const mapped = groupedTotals.map((item) => {
        const groupingValue = normalizeGroupingField(item, GROUPING_FIELDS);
        return {
          grouping: groupingValue,
          trend: item.trend || null,
          current_period: item.current_period || 0,
          previous_period: item.previous_period || 0,
          change: item.change ?? ((item.current_period || 0) - (item.previous_period || 0)),
        change_percentage: item.change_percentage ?? item.change_perc ?? calculateChangePercentage(item)
        };
      });
      return { 
        data: mapped, 
        ...(data.grouping ? { grouping: data.grouping } : {}) 
      };
    }

    // 3) Try common array locations
    const rawData = extractDataFromApiResponse(data, [
      'items',
      'details',
      'CloudCost.details',
      'breakdown',
      'ResourceList',
      'ResourceChanges',
      'resourceChanges'
    ]);

    // 4) Normalize: ensure each row has 'grouping'
    const normalizedData = rawData.map((row) => {
      if (row && (row.grouping !== undefined && row.grouping !== null)) {
        return row;
      }
      const groupingValue = normalizeGroupingField(row, GROUPING_FIELDS);
      return row ? { ...row, grouping: groupingValue } : { grouping: groupingValue };
    });

    return { 
      data: normalizedData, 
      ...(data.grouping ? { grouping: data.grouping } : {}) 
    };
  },
  apiData
);

/**
 * Transform data for AreaChart widget
 */
export const transformAreaChartData = (apiData) => safeDataMapping(
  (data) => ({
    data: data.timeseries || data.data || [],
    dateFormat: data.dateFormat || DATE_FORMATS.SHORT
  }),
  apiData
);

/**
 * Transform data for BarChart widget
 */
export const transformBarChartData = (apiData) => safeDataMapping(
  (data) => {
    if (Array.isArray(data?.data)) {
      return {
        data: data.data,
        dateFormat: data.dateFormat || DATE_FORMATS.LONG
      };
    }

    const details = extractDataFromApiResponse(data, [
      'details',
      'CloudCost.details',
      'items'
    ]);

    const mapped = details.map((d) => {
      if (d.grouping !== undefined && d.grouping !== null && d.grouping !== '') {
        return {
          time: extractTimeValue(d),
          grouping: d.grouping,
          measure: extractMeasureValue(d)
        };
      }
      
      const time = extractTimeValue(d);
      const grouping = normalizeGroupingField(d, [
        'organisation_name',
        'organization',
        'organisation',
        'cost_resource_type',
        'resource_type',
        'project_name',
        'project',
        'account',
        'name'
      ], d.groupings?.[1]?.value || 'Total');
      const measure = extractMeasureValue(d);
      
      return { time, grouping, measure };
    });

    return {
      data: mapped,
      dateFormat: data?.dateFormat || DATE_FORMATS.LONG
    };
  },
  apiData
);

/**
 * Transform data for DonutChart widget
 */
export const transformDonutChartData = (apiData) => safeDataMapping(
  (data) => {
    let rawData = [];
    
    if (Array.isArray(data)) {
      rawData = data;
    } else {
      rawData = extractDataFromApiResponse(data, [
        'details',
        'data',
        'CloudCost.details'
      ]);
    }
    
    const normalizedData = rawData.map(item => {
      if (item?.name !== undefined && item?.value !== undefined) {
        return item;
      }
      
      const normalizedItem = { ...item };
      
      if (normalizedItem.value === undefined) {
        normalizedItem.value = extractMeasureValue(normalizedItem);
      }
      
      if (normalizedItem.name === undefined) {
        normalizedItem.name = normalizeGroupingField(normalizedItem, [
          'project_name',
          'project',
          'cost_resource_type',
          'organisation_name',
          'organization',
          'label'
        ]);
      }
      
      return normalizedItem;
    });
    
    return {
      data: normalizedData
    };
  },
  apiData
);

/**
 * Transform data for SectionBreak widget (no-op)
 */
const EMPTY_SECTION_BREAK_DATA = Object.freeze({});
export const transformSectionBreakData = () => EMPTY_SECTION_BREAK_DATA;

/**
 * Transform data for ForecastChart widget
 */
export const transformForecastChartData = (apiData) => safeDataMapping(
  (data) => data, // The widget handles transformation
  apiData
);

/**
 * Transform data for ForecastTable widget
 */
export const transformForecastTableData = (apiData) => safeDataMapping(
  (data) => data, // The widget handles transformation
  apiData
);

/**
 * Transform data for ResourceChangesTrend widget
 */
export const transformResourceChangesTrendData = (apiData) => safeDataMapping(
  (data) => {
    const resourceChanges = data?.ResourceChanges || data?.resourceChanges || data?.data || [];
    
    if (Array.isArray(resourceChanges) && resourceChanges.length > 0) {
      const firstItem = resourceChanges[0];
      if (firstItem.date_used || firstItem.date || firstItem.time) {
        return {
          data: resourceChanges.map(item => ({
            ...item,
            date_used: item.date_used || item.date || item.time
          }))
        };
      }
    }
    
    return { data: resourceChanges };
  },
  apiData
);

/**
 * Transform data for ResourceChangesOverview widget
 */
export const transformResourceChangesOverviewData = (apiData) => safeDataMapping(
  (data) => {
    const resourceChanges = data?.ResourceChanges || data?.resourceChanges || data?.data || [];
    return { data: resourceChanges };
  },
  apiData
);

/**
 * Transform data for ResourceOverview widget
 */
export const transformResourceOverviewData = (apiData) => safeDataMapping(
  (data) => {
    const resourceList = data?.ResourceList || data?.data || [];
    return { ResourceList: resourceList };
  },
  apiData
);

/**
 * Transform data for ResourceList widget
 */
export const transformResourceListData = (apiData) => safeDataMapping(
  (data) => {
    const resourceList = data?.ResourceList || data?.data || [];
    return { ResourceList: resourceList };
  },
  apiData
);

/**
 * Transform data for ResourceUtilization widget
 */
export const transformResourceUtilizationData = (apiData) => safeDataMapping(
  (data) => {
    const resourceUtilization = data?.ResourceUtilization || data?.resourceUtilization || data;
    const segments = resourceUtilization?.segments || resourceUtilization?.Segments || [];
    
    const chartData = segments.map((segment) => ({
      start_time: segment.start_time || segment.StartTime,
      end_time: segment.end_time || segment.EndTime,
      cpu_utilization: segment.cpu_utilization || segment.CPUUtilization || 0,
      memory_utilization: segment.memory_utilization || segment.MemoryUtilization || 0,
      storage_utilization: segment.storage_utilization || segment.StorageUtilization || 0
    }));
    
    return { data: chartData };
  },
  apiData
);

/**
 * Transform data for InsightTable widget
 */
export const transformInsightTableData = (apiData) => safeDataMapping(
  (data) => {
    const insights = data?.Insights || data?.insights || data?.data || [];
    return { Insights: insights };
  },
  apiData
);

/**
 * Transform data for CostTrendChart widget
 */
export const transformCostTrendChartData = (apiData) => safeDataMapping(
  (data) => {
    const details = data?.CloudCost?.details || data?.details || data?.data || [];
    
    const transformedData = details.map((d) => {
      let time, grouping, measure;
      
      if (d.groupings && Array.isArray(d.groupings)) {
        time = d.groupings[0]?.value || d.time;
        grouping = d.groupings[1]?.value || d.grouping || 'Total';
        measure = d.measures?.[0]?.value ?? d.cost ?? d.measure ?? 0;
      } else {
        time = d.time || d.date;
        grouping = d.grouping || d.cost_resource_type || d.service || 
                   d.project_name || d.organisation_name || 'Total';
        measure = d.cost ?? d.measure ?? d.value ?? 0;
      }
      
      return { time, grouping, measure };
    });
    
    return {
      data: transformedData,
      dateFormat: data?.dateFormat || DATE_FORMATS.SHORT
    };
  },
  apiData
);

// ============================================================================
// Transformer Registry
// ============================================================================

/**
 * Map of widget types to their transform functions
 */
export const dataTransformers = {
  kpi: transformKPIData,
  table: transformTableData,
  areachart: transformAreaChartData,
  barchart: transformBarChartData,
  donutchart: transformDonutChartData,
  sectionbreak: transformSectionBreakData,
  forecastchart: transformForecastChartData,
  forecasttable: transformForecastTableData,
  resourcechangestrend: transformResourceChangesTrendData,
  resourcechangesoverview: transformResourceChangesOverviewData,
  resourceoverview: transformResourceOverviewData,
  resourcelist: transformResourceListData,
  resourceutilization: transformResourceUtilizationData,
  insighttable: transformInsightTableData,
  costtrendchart: transformCostTrendChartData
};

/**
 * Transform API data for a widget type
 * @param {string} type - Widget type
 * @param {Object} apiData - Raw API data
 * @returns {Object} - Transformed data
 */
export const transformWidgetData = (type, apiData) => {
  const transformer = dataTransformers[type];
  if (!transformer) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      // Warn during development to surface likely config typos
      const knownTypes = Object.keys(dataTransformers);
      // eslint-disable-next-line no-console
      console.warn(
        `No data transformer found for widget type "${type}". Known types: ${knownTypes.join(', ')}`
      );
    }
    return apiData;
  }
  return transformer(apiData);
};

export default transformWidgetData;

